import React, { useState, useEffect } from 'react';
import { UnterweisungenLayout } from './UnterweisungenLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  FileDown, 
  Loader2, 
  Calendar, 
  Users, 
  BookOpen,
  Filter,
  Download,
  FileText,
  Table,
  Check,
  X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const UnterweisungenExport = () => {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Data
  const [instructions, setInstructions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Filters
  const [exportType, setExportType] = useState('instruction'); // 'instruction' or 'employee'
  const [selectedInstructions, setSelectedInstructions] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'confirmed', 'pending'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [format, setFormat] = useState('pdf'); // 'pdf' or 'csv'
  const [sortBy, setSortBy] = useState('name'); // 'name', 'status', 'date'
  
  // Preview data
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [instructionsRes, employeesRes, locationsRes] = await Promise.all([
        axios.get(`${API}/api/instructions`, { headers }),
        axios.get(`${API}/api/admin/employees`, { headers }),
        axios.get(`${API}/api/admin/locations`, { headers })
      ]);
      
      setInstructions(instructionsRes.data || []);
      setEmployees(employeesRes.data || []);
      setLocations(locationsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAllInstructions = () => {
    if (selectedInstructions.length === instructions.length) {
      setSelectedInstructions([]);
    } else {
      setSelectedInstructions(instructions.map(i => i.id));
    }
  };

  const handleSelectAllEmployees = () => {
    if (selectedEmployees.length === employees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map(e => e.id));
    }
  };

  const handleSelectAllLocations = () => {
    if (selectedLocations.length === locations.length) {
      setSelectedLocations([]);
    } else {
      setSelectedLocations(locations.map(l => l.id));
    }
  };

  const handleInstructionToggle = (id) => {
    setSelectedInstructions(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleEmployeeToggle = (id) => {
    setSelectedEmployees(prev => 
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  const handleLocationToggle = (id) => {
    setSelectedLocations(prev => 
      prev.includes(id) ? prev.filter(l => l !== id) : [...prev, id]
    );
  };

  const handleQuickDateFilter = (days) => {
    const today = new Date();
    const from = new Date();
    
    if (days === 'all') {
      setDateFrom('');
      setDateTo('');
    } else {
      from.setDate(today.getDate() - days);
      setDateFrom(from.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }
  };

  const handlePreview = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {
        export_type: exportType,
        status_filter: statusFilter,
        date_from: dateFrom,
        date_to: dateTo,
        instruction_ids: exportType === 'instruction' ? selectedInstructions.join(',') : '',
        employee_ids: exportType === 'employee' ? selectedEmployees.join(',') : '',
        location_ids: selectedLocations.join(','),
        employee_ids: exportType === 'employee' ? selectedEmployees.join(',') : '',
        sort_by: sortBy
      };
      
      const response = await axios.get(`${API}/api/admin/instructions/export/preview`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params
      });
      
      setPreviewData(response.data);
    } catch (error) {
      console.error('Failed to fetch preview:', error);
      toast.error('Fehler beim Laden der Vorschau');
    }
  };

  const handleExport = async () => {
    if (exportType === 'instruction' && selectedInstructions.length === 0) {
      toast.error('Bitte wählen Sie mindestens eine Unterweisung aus');
      return;
    }
    if (exportType === 'employee' && selectedEmployees.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Mitarbeiter aus');
      return;
    }

    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const params = {
        export_type: exportType,
        status_filter: statusFilter,
        date_from: dateFrom,
        date_to: dateTo,
        instruction_ids: exportType === 'instruction' ? selectedInstructions.join(',') : '',
        employee_ids: exportType === 'employee' ? selectedEmployees.join(',') : '',
        location_ids: selectedLocations.join(','),
        sort_by: sortBy,
        format: format
      };
      
      const response = await axios.get(`${API}/api/admin/instructions/export`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params,
        responseType: 'blob'
      });
      
      // Download file – revokeObjectURL nach 30s (verhindert Chrome White-Screen)
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const extension = format === 'pdf' ? 'pdf' : 'csv';
      const filename = `unterweisungen_export_${new Date().toISOString().split('T')[0]}.${extension}`;
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 30000);
      
      toast.success(`✅ Export erfolgreich heruntergeladen: ${filename}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Fehler beim Exportieren');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <UnterweisungenLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </UnterweisungenLayout>
    );
  }

  return (
    <UnterweisungenLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">Unterweisungen Export</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Exportieren Sie Unterweisungsdaten nach Ihren Kriterien</p>
        </div>

        {/* Export Type Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Export-Typ auswählen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => setExportType('instruction')}
                className={`p-2 sm:p-4 rounded-lg border-2 transition-all ${
                  exportType === 'instruction'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <BookOpen className={`h-5 w-5 sm:h-8 sm:w-8 mx-auto mb-1 sm:mb-2 ${
                  exportType === 'instruction' ? 'text-blue-600' : 'text-gray-600'
                }`} />
                <div className="font-semibold text-xs sm:text-base">Pro Unterweisung</div>
                <div className="text-[10px] sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5 sm:mt-1">
                  Exportieren Sie nach Unterweisungen gruppiert
                </div>
              </button>
              
              <button
                onClick={() => setExportType('employee')}
                className={`p-2 sm:p-4 rounded-lg border-2 transition-all ${
                  exportType === 'employee'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <Users className={`h-5 w-5 sm:h-8 sm:w-8 mx-auto mb-1 sm:mb-2 ${
                  exportType === 'employee' ? 'text-blue-600' : 'text-gray-600'
                }`} />
                <div className="font-semibold text-xs sm:text-base">Pro Mitarbeiter</div>
                <div className="text-[10px] sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5 sm:mt-1">
                  Exportieren Sie nach Mitarbeitern gruppiert
                </div>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filter & Optionen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setStatusFilter('all')}
                  className={`px-2 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    statusFilter === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Alle
                </button>
                <button
                  onClick={() => setStatusFilter('confirmed')}
                  className={`px-2 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
                    statusFilter === 'confirmed'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Check className="h-3 w-3 sm:h-4 sm:w-4" />
                  Erledigt
                </button>
                <button
                  onClick={() => setStatusFilter('pending')}
                  className={`px-2 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
                    statusFilter === 'pending'
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <X className="h-3 w-3 sm:h-4 sm:w-4" />
                  Offen
                </button>
              </div>
            </div>

            {/* Location Filter */}
            {locations.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Standorte
                </label>
                <div className="space-y-2">
                  <button
                    onClick={handleSelectAllLocations}
                    className="w-full px-3 py-2 text-xs sm:text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
                  >
                    {selectedLocations.length === locations.length ? 'Alle abwählen' : 'Alle auswählen'}
                  </button>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-gray-200 dark:border-gray-700 rounded-lg">
                    {locations.map((location) => (
                      <label
                        key={location.id}
                        className="flex items-start gap-2 p-2 hover:bg-gray-50 dark:bg-gray-800 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedLocations.includes(location.id)}
                          onChange={() => handleLocationToggle(location.id)}
                          className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{location.name}</p>
                          <p className="text-xs text-gray-500 truncate">{location.street}, {location.city}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                Zeitraum
              </label>
              
              {/* Quick Filters */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                <button
                  onClick={() => handleQuickDateFilter(7)}
                  className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Letzte 7 Tage
                </button>
                <button
                  onClick={() => handleQuickDateFilter(30)}
                  className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Letzter Monat
                </button>
                <button
                  onClick={() => handleQuickDateFilter(365)}
                  className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Dieses Jahr
                </button>
                <button
                  onClick={() => handleQuickDateFilter('all')}
                  className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Alle Zeit
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Von</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Bis</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sortierung</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="name">Alphabetisch (Name)</option>
                <option value="status">Nach Status</option>
                <option value="date">Nach Datum</option>
              </select>
            </div>

            {/* Format Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Export-Format</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setFormat('pdf')}
                  className={`px-2 py-2 sm:px-4 sm:py-3 rounded-lg border-2 transition-all flex items-center justify-center gap-1.5 sm:gap-2 ${
                    format === 'pdf'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="font-medium text-xs sm:text-base">PDF</span>
                </button>
                <button
                  onClick={() => setFormat('csv')}
                  className={`px-2 py-2 sm:px-4 sm:py-3 rounded-lg border-2 transition-all flex items-center justify-center gap-1.5 sm:gap-2 ${
                    format === 'csv'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Table className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="font-medium text-xs sm:text-base">CSV / Excel</span>
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Selection */}
        {exportType === 'instruction' ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Unterweisungen auswählen</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAllInstructions}
                >
                  {selectedInstructions.length === instructions.length ? 'Keine auswählen' : 'Alle auswählen'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {instructions.map(instruction => (
                  <label
                    key={instruction.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:bg-gray-800 cursor-pointer border border-gray-200 dark:border-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={selectedInstructions.includes(instruction.id)}
                      onChange={() => handleInstructionToggle(instruction.id)}
                      className="h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{instruction.title}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{instruction.category}</div>
                    </div>
                  </label>
                ))}
              </div>
              {instructions.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Keine Unterweisungen gefunden
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Mitarbeiter auswählen</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAllEmployees}
                >
                  {selectedEmployees.length === employees.length ? 'Keine auswählen' : 'Alle auswählen'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {employees.map(employee => (
                  <label
                    key={employee.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:bg-gray-800 cursor-pointer border border-gray-200 dark:border-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={selectedEmployees.includes(employee.id)}
                      onChange={() => handleEmployeeToggle(employee.id)}
                      className="h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{employee.name}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{employee.email}</div>
                    </div>
                  </label>
                ))}
              </div>
              {employees.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Keine Mitarbeiter gefunden
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Preview Button */}
        <Card>
          <CardContent className="p-4">
            <Button
              onClick={handlePreview}
              variant="outline"
              className="w-full"
            >
              <FileText className="h-4 w-4 mr-2" />
              Vorschau anzeigen
            </Button>
            
            {previewData && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="font-semibold text-blue-900 mb-2">Export-Vorschau:</div>
                <div className="text-sm text-blue-800 space-y-1">
                  <div>• {previewData.instruction_count} Unterweisung(en)</div>
                  <div>• {previewData.employee_count} Mitarbeiter</div>
                  <div>• {previewData.total_entries} Einträge gesamt</div>
                  <div>• {previewData.confirmed_count} bestätigt ({previewData.confirmation_rate}%)</div>
                  <div>• {previewData.pending_count} offen</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Export Button */}
        <Card>
          <CardContent className="p-4">
            <Button
              onClick={handleExport}
              disabled={exporting || (exportType === 'instruction' && selectedInstructions.length === 0) || (exportType === 'employee' && selectedEmployees.length === 0)}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
            >
              {exporting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Exportiere...
                </>
              ) : (
                <>
                  <Download className="h-5 w-5 mr-2" />
                  {format === 'pdf' ? 'PDF exportieren' : 'CSV exportieren'}
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </UnterweisungenLayout>
  );
};
